import type { SocialMediaV2 } from "@/domains/company/company/api/types";
import { normalizeLinkedInCompanyUrl } from "@/domains/company/onboarding/utils/linkedin-url";

/**
 * The social profiles the settings page offers, as keys of the `social_media`
 * jsonb. Lowercase because that is what the previous page wrote.
 *
 * `linkedin` is deliberately not among them any more — see {@link MIGRATED_KEY}.
 */
export const COMPANY_SOCIAL_KEYS = [
  "twitter",
  "github",
  "instagram",
  "facebook",
] as const;

export type CompanySocialKey = (typeof COMPANY_SOCIAL_KEYS)[number];

/**
 * Where the previous page put the LinkedIn URL, and where it stops being put.
 *
 * One address with two homes is one address that can disagree with itself, and
 * every consumer then has to pick a winner (I10). The column wins: it is typed,
 * the backend validates it as a company page (`linkedinCompanyUrlSchema`), and
 * the LinkedIn sync already writes it — the jsonb key was only ever the place
 * a free-text social input happened to land.
 *
 * The removal lives here, inside the one function every write of this object
 * passes through, rather than at a call site: that way there is no path that
 * saves the object and leaves the duplicate behind. It is a declared data
 * change, not a side effect — the section that writes this column hands over
 * `linkedin_url` in the same payload, so the URL is never dropped, only moved.
 */
const MIGRATED_KEY = "linkedin";

/**
 * Removing a social removes its **key**, it does not set it to `null`.
 *
 * The previous page wrote `null`, so the object accumulated dead keys forever
 * and any consumer iterating it had to filter them out — a public page that
 * forgets to would render an empty icon link (EC21). Sanitising on the way out
 * means the object only ever holds live values.
 */
export const sanitizeSocialMedia = (
  social: SocialMediaV2 | null | undefined,
): SocialMediaV2 | null => {
  if (!social) return null;
  const clean: SocialMediaV2 = {};
  for (const [key, value] of Object.entries(social)) {
    if (key === MIGRATED_KEY) continue;
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (trimmed.length > 0) clean[key] = trimmed;
  }
  return Object.keys(clean).length > 0 ? clean : null;
};

/** Applies one edit to the object and hands back what should reach the column. */
export const withSocialLink = (
  social: SocialMediaV2 | null | undefined,
  key: CompanySocialKey,
  url: string,
): SocialMediaV2 | null =>
  sanitizeSocialMedia({ ...(social ?? {}), [key]: url });

/**
 * What the dedicated LinkedIn field shows.
 *
 * Reading the key the column replaces is the soft half of the migration: a
 * company saved before this field existed sees its own URL, instead of an empty
 * field that invites typing it back in — and the first save that carries the
 * card consolidates it onto the column.
 */
export const seedLinkedinUrl = (company: {
  linkedin_url?: string | null;
  social_media?: SocialMediaV2 | null;
}): string =>
  company.linkedin_url?.trim() ||
  company.social_media?.[MIGRATED_KEY]?.trim() ||
  "";

/**
 * What the `linkedin_url` column should hold for what is on screen.
 *
 * Normalising here rather than on screen keeps the rule this page follows
 * everywhere: what the user typed is never rewritten under them, only what
 * leaves is tidied (EC8). It runs on both sides of the dirty check, so an
 * address stored with a locale subdomain does not read as changed the moment
 * the section reports it back untouched.
 */
export const linkedinColumnValue = (value: string): string | null => {
  const trimmed = value.trim();
  // `null`, not `""`: the backend column is `min(1).nullable()`, so the empty
  // string is a 400 and `null` is the only way to clear it.
  if (trimmed === "") return null;
  // What cannot be normalised is handed back as typed. Dropping it would make
  // the field read as unchanged and let the save sail past a value still on
  // screen; kept, it stays dirty and the page refuses to save until it is a
  // company page or empty.
  return normalizeLinkedInCompanyUrl(trimmed) ?? trimmed;
};
